import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  ExpandMore as ExpandMoreIcon,
  FolderOpen as FolderIcon,
  AccountTree as TreeIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { CONFIG } from '../../config/config';
import { getToken } from '../../services/auth';
import TitleBar from '../../components/TitleBar/TitleBar';
import DashboardFilters from './DashboardFilters';
import DemographicsTab from './DemographicsTab';
import MetricsTab from './MetricsTab';
import ReviewStatusTab from './ReviewStatusTab';
import AttributeDemographicsTab from './AttributeDemographicsTab';
import EntityHierarchyTab from './EntityHierarchyTab';
import RAIDsTab from './RAIDsTab';
import {
  getUserDashboardSummary,
  UserDashboardSummaryResponse,
  UserDirectEntity,
  getChildCategoriesWithCounts,
  CategoryWithCount,
  getReviewQuestionnaireWorkflow,
  ReviewQuestionnaireWorkflowResponse,
} from '../../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export interface EntityType {
  id: number;
  name: string;
  display_label: string;
  category: string;
  description?: string;
}

export interface UserEntityInfo {
  id: number;
  name: string;
  entity_type_id: number;
  entity_type_name: string;
  entity_type_display_label: string;
  category: string;
  parent_ids: number[];
  has_children: boolean;
}

export interface UserEntityHierarchy {
  entities: UserEntityInfo[];
  categories: string[];
  hierarchy: Record<string, Record<string, {
    entity_type_id: number;
    entity_type_name: string;
    entity_type_display_label: string;
    entities: { id: number; name: string; has_children: boolean }[];
  }>>;
  top_level_entities: UserEntityInfo[];
}

const Dashboard: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Left panel collapsed state
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  
  // Selected entity from Quick Search
  const [selectedEntityId, setSelectedEntityId] = useState<number | null>(null);
  const [selectedEntityName, setSelectedEntityName] = useState<string | null>(null);

  // User's entity hierarchy data
  const [userHierarchy, setUserHierarchy] = useState<UserEntityHierarchy | null>(null);
  
  // User dashboard summary data
  const [dashboardSummary, setDashboardSummary] = useState<UserDashboardSummaryResponse | null>(null);
  const [questionnaireWorkflow, setQuestionnaireWorkflow] = useState<ReviewQuestionnaireWorkflowResponse | null>(null);
  
  // Child categories for the selected entity (to determine if leaf)
  const [childCategories, setChildCategories] = useState<string[]>([]);
  
  // Category-based navigation state
  const [availableCategories, setAvailableCategories] = useState<CategoryWithCount[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  
  // Entity selection accordion expanded state - collapse after entity is selected
  const [entityAccordionExpanded, setEntityAccordionExpanded] = useState<string | false>('owner');

  // Fetch user's dashboard summary and entity hierarchy on mount
  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      try {
        // Fetch dashboard summary using new API
        const summaryResponse = await getUserDashboardSummary();
        setDashboardSummary(summaryResponse.data);
        try {
          const workflowResponse = await getReviewQuestionnaireWorkflow();
          setQuestionnaireWorkflow(workflowResponse.data);
        } catch {
          setQuestionnaireWorkflow(null);
        }
        
        // Also fetch legacy hierarchy for filters
        const token = getToken();
        const response = await fetch(`${CONFIG.SERVER_URL}/dashboard/my-entities`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) throw new Error('Failed to fetch user entities');
        const data: UserEntityHierarchy = await response.json();
        setUserHierarchy(data);
        
        // If user has only one top-level entity, auto-select it
        if (data.top_level_entities.length === 1) {
          const entity = data.top_level_entities[0];
          setSelectedEntityId(entity.id);
          setSelectedEntityName(entity.name ?? null);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch user data');
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, []);

  // Fetch child categories when entity changes (to determine if leaf entity)
  useEffect(() => {
    if (!selectedEntityId) {
      setChildCategories([]);
      setAvailableCategories([]);
      setSelectedCategory(null);
      return;
    }
    const fetchChildCategories = async () => {
      try {
        // Fetch basic child categories for leaf detection
        const response = await fetch(
          `${CONFIG.SERVER_URL}/dashboard/child-categories?parent_entity_id=${selectedEntityId}`
        );
        if (!response.ok) throw new Error('Failed to fetch child categories');
        const data = await response.json();
        setChildCategories(data.categories.map((c: { category: string }) => c.category));
        
        // Fetch categories with counts for category selector
        const categoriesResponse = await getChildCategoriesWithCounts(selectedEntityId);
        setAvailableCategories(categoriesResponse.data.categories || []);
        
        // Auto-select first category if available
        if (categoriesResponse.data.categories && categoriesResponse.data.categories.length > 0) {
          setSelectedCategory(categoriesResponse.data.categories[0].category);
        } else {
          setSelectedCategory(null);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch child data');
      }
    };
    fetchChildCategories();
  }, [selectedEntityId]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEntitySelect = useCallback((entity: { id: number; entity_type_id: number; category: string }) => {
    setSelectedEntityId(entity.id);
    // Find entity name from hierarchy
    const foundEntity = userHierarchy?.entities.find(e => e.id === entity.id);
    setSelectedEntityName(foundEntity?.name || null);
    // Collapse entity selection accordions after selection
    setEntityAccordionExpanded(false);
  }, [userHierarchy]);

  // Simple handler that accepts just entityId for hierarchy/demographics navigation
  const handleEntityIdSelect = useCallback((entityId: number) => {
    setSelectedEntityId(entityId);
    // Find entity name from hierarchy
    const foundEntity = userHierarchy?.entities.find(e => e.id === entityId);
    setSelectedEntityName(foundEntity?.name || null);
  }, [userHierarchy]);

  // Check if entity is a leaf (no children) - show dashboard for the entity itself
  const isLeafEntity = selectedEntityId !== null && childCategories.length === 0;

  const selectedEntityCategory = useMemo(() => {
    if (!selectedEntityId || !userHierarchy) return null;
    return userHierarchy.entities.find((e) => e.id === selectedEntityId)?.category ?? null;
  }, [selectedEntityId, userHierarchy]);
  
  // For now, always show dashboard when entity is selected (leaf entities show their own data)
  const isFilterComplete = selectedEntityId !== null;

  // Helper to render entity list by role
  const renderEntityList = (entities: UserDirectEntity[], role: string) => {
    if (!entities || entities.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No entities as {role}
        </Typography>
      );
    }
    return (
      <List dense>
        {entities.map((entity) => (
          <ListItem
            key={entity.id}
            button
            onClick={() => handleEntitySelect({ id: entity.id, entity_type_id: entity.review_entity_type_id, category: entity.entity_type_category || '' })}
            selected={selectedEntityId === entity.id}
            sx={{ borderRadius: 1, mb: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <BusinessIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText
              primary={entity.name}
              secondary={entity.entity_type_display_label}
              primaryTypographyProps={{ variant: 'body2' }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <TitleBar />
      <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', gap: 2, p: 2 }}>
        {/* Left Panel - User's Entities */}
        <Paper sx={{ 
          width: leftPanelCollapsed ? 48 : 320, 
          flexShrink: 0, 
          p: leftPanelCollapsed ? 1 : 2, 
          overflow: 'auto',
          transition: 'width 0.3s ease',
          position: 'relative'
        }}>
          {/* Collapse/Expand Button */}
          <Tooltip title={leftPanelCollapsed ? 'Expand panel' : 'Collapse panel'} placement="right">
            <IconButton
              size="small"
              onClick={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
              sx={{ 
                position: leftPanelCollapsed ? 'static' : 'absolute',
                right: leftPanelCollapsed ? 'auto' : 8,
                top: leftPanelCollapsed ? 'auto' : 8,
                zIndex: 1
              }}
            >
              {leftPanelCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
          
          {!leftPanelCollapsed && (
            <>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 4 }}>
            <TreeIcon color="primary" />
            My Entities
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : dashboardSummary ? (
            <>
              {/* Summary Cards */}
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Card variant="outlined" sx={{ bgcolor: 'primary.50' }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="h5" color="primary.main">
                        {dashboardSummary.direct_entities.total}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Direct Entities
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6}>
                  <Card variant="outlined" sx={{ bgcolor: 'secondary.50' }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="h5" color="secondary.main">
                        {dashboardSummary.total_entities_with_children}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total (with children)
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Entities by Role - controlled accordions that collapse after entity selection */}
              <Accordion 
                expanded={entityAccordionExpanded === 'owner'} 
                onChange={(_, isExpanded) => setEntityAccordionExpanded(isExpanded ? 'owner' : false)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" color="success" />
                    <Typography variant="subtitle2">
                      As Owner
                    </Typography>
                    <Chip label={dashboardSummary.direct_entities.as_owner} size="small" color="success" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {renderEntityList(dashboardSummary.entities_by_role.owner, 'owner')}
                </AccordionDetails>
              </Accordion>

              <Accordion 
                expanded={entityAccordionExpanded === 'reviewer'} 
                onChange={(_, isExpanded) => setEntityAccordionExpanded(isExpanded ? 'reviewer' : false)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupIcon fontSize="small" color="info" />
                    <Typography variant="subtitle2">
                      As Reviewer
                    </Typography>
                    <Chip label={dashboardSummary.direct_entities.as_reviewer} size="small" color="info" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {renderEntityList(dashboardSummary.entities_by_role.reviewer, 'reviewer')}
                </AccordionDetails>
              </Accordion>

              <Accordion 
                expanded={entityAccordionExpanded === 'reviewee'} 
                onChange={(_, isExpanded) => setEntityAccordionExpanded(isExpanded ? 'reviewee' : false)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FolderIcon fontSize="small" color="warning" />
                    <Typography variant="subtitle2">
                      As Reviewee
                    </Typography>
                    <Chip label={dashboardSummary.direct_entities.as_reviewee} size="small" color="warning" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {renderEntityList(dashboardSummary.entities_by_role.reviewee, 'reviewee')}
                </AccordionDetails>
              </Accordion>

              <Divider sx={{ my: 2 }} />

              {/* Category Counts */}
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FolderIcon fontSize="small" />
                By Category
              </Typography>
              {dashboardSummary.counts_by_category.map((cat, idx) => (
                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="body2">{cat.category || cat.entity_type || 'Unknown'}</Typography>
                  <Chip label={cat.count} size="small" variant="outlined" />
                </Box>
              ))}
              
              {/* Category Selector for Dashboard View */}
              {selectedEntityId && availableCategories.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TreeIcon fontSize="small" color="secondary" />
                    View By Category
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Select a category to see breakdown by each entity of that type
                  </Typography>
                  {availableCategories.map((cat) => (
                    <Chip
                      key={cat.category}
                      label={`${cat.category} (${cat.entity_count})`}
                      onClick={() => setSelectedCategory(cat.category)}
                      color={selectedCategory === cat.category ? 'primary' : 'default'}
                      variant={selectedCategory === cat.category ? 'filled' : 'outlined'}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5, cursor: 'pointer' }}
                    />
                  ))}
                </>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No entities found
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />
          
          {/* Quick Search */}
          <Typography variant="subtitle2" gutterBottom>
            Quick Search
          </Typography>
          <DashboardFilters
            onEntitySelect={handleEntitySelect}
            userEntities={userHierarchy?.entities || []}
          />
            </>
          )}
        </Paper>

        {/* Right Panel - Dashboard Content */}
        <Paper sx={{ flex: 1, overflow: 'auto' }}>
          {error && (
            <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {!isFilterComplete ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                Select an entity to view dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Click on an entity from the left panel or use the search.
              </Typography>
            </Box>
          ) : (
            <>
              {/* Entity Name Header */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  {selectedEntityName || `Entity #${selectedEntityId}`}
                </Typography>
              </Box>

              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="dashboard tabs">
                  <Tab label="Entities" id="dashboard-tab-0" />
                  <Tab label="Hierarchy" id="dashboard-tab-1" />
                  <Tab label="Demographics" id="dashboard-tab-2" />
                  <Tab label="Metrics" id="dashboard-tab-3" />
                  <Tab label="Reviews" id="dashboard-tab-4" />
                  <Tab label="RAIDs" id="dashboard-tab-5" />
                </Tabs>
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <TabPanel value={tabValue} index={0}>
                    <DemographicsTab
                      parentEntityId={selectedEntityId!}
                      childCategory={childCategories[0] || ''}
                      isLeafEntity={isLeafEntity}
                      onEntitySelect={handleEntityIdSelect}
                      onNavigateToTab={setTabValue}
                    />
                  </TabPanel>
                  <TabPanel value={tabValue} index={1}>
                    <EntityHierarchyTab
                      parentEntityId={selectedEntityId!}
                      onEntitySelect={handleEntityIdSelect}
                      onEntityNameFound={setSelectedEntityName}
                    />
                  </TabPanel>
                  <TabPanel value={tabValue} index={2}>
                    <AttributeDemographicsTab
                      parentEntityId={selectedEntityId!}
                      isLeafEntity={isLeafEntity}
                      selectedCategory={selectedCategory}
                      availableCategories={availableCategories}
                      onCategoryChange={setSelectedCategory}
                    />
                  </TabPanel>
                  <TabPanel value={tabValue} index={3}>
                    <MetricsTab
                      parentEntityId={selectedEntityId!}
                      childEntityTypes={[]}
                      isLeafEntity={isLeafEntity}
                      entityTypeId={null}
                      selectedCategory={selectedCategory}
                    />
                  </TabPanel>
                  <TabPanel value={tabValue} index={4}>
                    {questionnaireWorkflow && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Review overview
                        </Typography>
                        <Grid container spacing={1.5}>
                          <Grid item xs={6} sm={3}>
                            <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                              <Typography variant="h6">{questionnaireWorkflow.draft.length}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Draft / in progress
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                              <Typography variant="h6">{questionnaireWorkflow.submitted.length}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Submitted (awaiting close)
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1,
                                textAlign: 'center',
                                borderColor:
                                  questionnaireWorkflow.submission_overdue.length > 0 ? 'error.main' : 'divider',
                              }}
                            >
                              <Typography
                                variant="h6"
                                color={
                                  questionnaireWorkflow.submission_overdue.length > 0 ? 'error' : 'text.primary'
                                }
                              >
                                {questionnaireWorkflow.submission_overdue.length}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Past submission due
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1,
                                textAlign: 'center',
                                borderColor:
                                  questionnaireWorkflow.closure_overdue.length > 0 ? 'error.main' : 'divider',
                              }}
                            >
                              <Typography
                                variant="h6"
                                color={questionnaireWorkflow.closure_overdue.length > 0 ? 'error' : 'text.primary'}
                              >
                                {questionnaireWorkflow.closure_overdue.length}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Past closure due
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                    <ReviewStatusTab
                      parentEntityId={selectedEntityId!}
                      childCategory={childCategories[0] || ''}
                      isLeafEntity={isLeafEntity}
                      entityTypeCategory={selectedEntityCategory}
                    />
                  </TabPanel>
                  <TabPanel value={tabValue} index={5}>
                    <RAIDsTab
                      parentEntityId={selectedEntityId!}
                      childCategory={childCategories[0] || ''}
                      isLeafEntity={isLeafEntity}
                      entityTypeCategory={selectedEntityCategory}
                    />
                  </TabPanel>
                </>
              )}
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default Dashboard;
